import { useEffect, useState } from "react";
import { Box, Typography, Button, Container, IconButton, Chip } from "@mui/material";
import { Trans } from "@lingui/react/macro";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import FileTitle from "@/components/ui/FileTitle";
import { useDealContext } from "@/contexts/DealContext";

const UploadPage = () => {
  const [documents, setDocuments] = useState<File[]>([]);
  const { deal, uploadFiles, docError, setDocError } = useDealContext();
  const [loading, setLoading] = useState(false);
  const onDrop = (acceptedFiles: File[]) => {
    setDocuments([...documents, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    multiple: true,
  });

  useEffect(() => {
    if (docError.length > 0) {
      setLoading(false);
    }
  }, [docError]);

  const handleStartUpload = () => {
    if (documents.length > 0) {
      setLoading(true);
      setDocError([]);
      uploadFiles(documents);
    }
  };

  const removeFile = (fileName: string) => {
    setDocuments(documents.filter((doc) => doc.name !== fileName));
    setDocError(docError.filter((doc) => doc !== fileName.split(".")[0]));
  };

  console.log("deal", deal);
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        padding: "1rem 1rem 0 0",
        position: "relative",
        display: "flex",
        gap: "1rem",
      }}
    >
      <Box
        overflow="auto"
        height="auto"
        sx={{ backgroundColor: "white" }}
        flexShrink={0} // prevent shrinking
      >
        <Typography
          variant="body2"
          sx={{ fontSize: "0.8rem", color: "text.secondary", marginTop: "0.25rem" }}
        >
          Description
        </Typography>
        <h3 style={{ margin: 0 }}>{deal.name}</h3>
        {deal.loans &&
          deal.loans.length > 0 &&
          deal.loans
            .filter((l) => !l.tranches)
            .map((loan) => {
              return (
                <Box key={loan.id}>
                  <Chip
                    sx={{ borderRadius: "4px", fontSize: "0.8rem" }}
                    key={loan.id}
                    size="small"
                    label={loan.name}
                  />
                </Box>
              );
            })}
      </Box>

      <Container maxWidth="sm" sx={{ textAlign: "left", mt: 8, pl: 0, pr: 0 }}>
        <Typography variant="h6" gutterBottom>
          <Trans>Upload your loan agreement(s)</Trans>
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <Trans>To begin IA data extraction, please upload the first loan agreement</Trans>
        </Typography>

        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed #1976d2",
            padding: 4,
            textAlign: "center",
            borderRadius: 2,
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
            mb: 2,
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            <Trans>Drag and drop files here or click here to select them</Trans>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <Trans>Accepted formats: .pdf, .docx, .txt</Trans>
          </Typography>
        </Box>

        {documents.map((doc) => (
          <div key={doc.name}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <FileTitle
                title={doc.name}
                mimeType={doc.type}
                url={URL.createObjectURL(doc)}
                fileSize={parseFloat((doc.size / 1024).toFixed(2))}
                isCorporate={false}
                isUnread={false}
                directory={undefined}
              />
              <IconButton size="small" color="inherit" onClick={() => removeFile(doc.name)}>
                <CloseIcon fontSize="small" color="error" />
              </IconButton>
            </div>
            {docError && docError.find((d) => d === doc.name.split(".")[0]) && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                An error occurred while uploading {doc.name}. Please check the file format and try
                again. Do not use scanned documents.
              </Typography>
            )}
          </div>
        ))}

        <div
          style={{
            width: "100%",
            marginTop: "8px",
            marginBottom: "4px",
            fontSize: "0.8rem",
            bottom: 0,
          }}
        >
          <b>⚠️ File Upload Notice</b>
          <br /> To prevent upload issues, <b>please rename your file to avoid double extensions</b>
          <br />
          (e.g., document.pdf.exe, file.docx.txt).
          <br />
          Files with multiple extensions may be <b>blocked for security reasons.</b>
          <br /> ✅ Use a single, correct extension like filename.pdf or report.docx before
          uploading.
        </div>

        <Button
          style={{ marginTop: "20px" }}
          variant="contained"
          color="primary"
          onClick={handleStartUpload}
          disabled={documents.length === 0 || loading}
        >
          <Trans>Start upload</Trans>
        </Button>
      </Container>
    </Box>
  );
};

export default UploadPage;
